import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { CheckoutAcceptance } from '@/lib/paymentTerms';

// ─── Types ───

export type PlanId = 'gratis' | 'pro' | 'enterprise';
export type BillingCycle = 'monthly' | 'yearly';

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  pdfUrl: string;
  description: string;
}

export interface UsageStats {
  parcelsUsed: number;
  parcelsLimit: number;
  apiCalls: number;
  apiCallsLimit: number;
  storageMb: number;
  storageLimitMb: number;
  surveysThisMonth: number;
  surveysLimit: number;
}

export interface PaymentMethod {
  type: 'card';
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface PlanInfo {
  id: PlanId;
  name: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
}

export const PLANS: PlanInfo[] = [
  {
    id: 'gratis',
    name: 'Gratis',
    price: 0,
    currency: 'kr',
    interval: 'månad',
    features: [
      '1 skogsfastighet',
      'Satellitövervakning',
      'Grundläggande AI-analys',
      'Community-åtkomst',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 249,
    currency: 'kr',
    interval: 'månad',
    features: [
      'Upp till 10 skogsfastigheter',
      'Drönare + satellitövervakning',
      'Full AI-kompanjon',
      'Prioriterad support',
      'Dataexport (CSV, GeoJSON)',
      'PDF-rapporter',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: -1,
    currency: 'kr',
    interval: 'månad',
    features: [
      'Obegränsat antal fastigheter',
      'API-åtkomst',
      'Anpassade integrationer',
      'Dedikerad support',
      'SLA-garanti',
      'On-premise möjlighet',
    ],
  },
];

// ─── Usage limits per plan ───

const PLAN_LIMITS: Record<PlanId, Pick<UsageStats, 'parcelsLimit' | 'apiCallsLimit' | 'storageLimitMb' | 'surveysLimit'>> = {
  gratis: { parcelsLimit: 1, apiCallsLimit: 100, storageLimitMb: 256, surveysLimit: 3 },
  pro: { parcelsLimit: 10, apiCallsLimit: 5000, storageLimitMb: 2048, surveysLimit: 20 },
  enterprise: { parcelsLimit: 9999, apiCallsLimit: 100000, storageLimitMb: 51200, surveysLimit: 9999 },
};

// ─── Subscription status type ───

export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'cancelling'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'
  | null;

// ─── Store ───

export interface QuotaError {
  resource: string;
  used: number;
  limit: number;
}

interface BillingState {
  currentPlan: PlanId;
  billingCycle: BillingCycle;
  nextBillingDate: string | null;
  invoices: Invoice[];
  usage: UsageStats;
  paymentMethod: PaymentMethod | null;
  stripeCustomerId: string | null;
  subscriptionStatus: SubscriptionStatus;
  trialDaysRemaining: number | null;
  quotaError: QuotaError | null;
  loading: boolean;
  error: string | null;

  isPlanActive: () => boolean;
  loadBillingData: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  changePlan: (planId: PlanId, acceptance?: CheckoutAcceptance) => Promise<void>;
  updatePaymentMethod: () => Promise<void>;
  cancelSubscription: () => Promise<void>;
  setQuotaError: (err: QuotaError | null) => void;
  handleApiError: (error: unknown) => boolean;
}

async function invokeEdgeFunction<T = unknown>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, {
    body,
  });
  if (error) {
    throw new Error(error.message ?? `Fel vid anrop till ${name}`);
  }
  return data as T;
}

export const useBillingStore = create<BillingState>((set, get) => ({
  currentPlan: 'gratis',
  billingCycle: 'monthly',
  nextBillingDate: null,
  invoices: [],
  usage: {
    parcelsUsed: 0,
    parcelsLimit: 1,
    apiCalls: 0,
    apiCallsLimit: 100,
    storageMb: 0,
    storageLimitMb: 256,
    surveysThisMonth: 0,
    surveysLimit: 3,
  },
  paymentMethod: null,
  stripeCustomerId: null,
  subscriptionStatus: null,
  trialDaysRemaining: null,
  quotaError: null,
  loading: false,
  error: null,

  // Returns true only when the subscription is active or trialing
  isPlanActive: () => {
    const { subscriptionStatus, currentPlan } = get();
    // Gratis plan is always "active" (no subscription needed)
    if (currentPlan === 'gratis') return true;
    return subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  },

  // ─── Load billing data from Supabase ───
  loadBillingData: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ loading: false, error: 'Inte inloggad' });
        return;
      }

      // Fetch subscription record
      const { data: subscription, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (subError) throw subError;

      const plan: PlanId = subscription?.plan ?? 'gratis';
      const status: SubscriptionStatus = subscription?.status ?? null;
      const limits = PLAN_LIMITS[plan];

      // Fetch usage stats
      const { data: usageData } = await supabase
        .from('usage_stats')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      // Fetch invoices
      const { data: invoiceRows } = await supabase
        .from('invoices')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(12);

      const invoices: Invoice[] = (invoiceRows ?? []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        date: row.date as string,
        amount: row.amount as number,
        currency: (row.currency as string) ?? 'SEK',
        status: row.status as Invoice['status'],
        pdfUrl: (row.pdf_url as string) ?? '#',
        description: row.description as string,
      }));

      // Compute trial days remaining
      const periodEnd = subscription?.current_period_end;
      let trialDays: number | null = null;
      if (status === 'trialing' && periodEnd) {
        trialDays = Math.max(0, Math.ceil((new Date(periodEnd).getTime() - Date.now()) / 86_400_000));
      }

      set({
        currentPlan: plan,
        subscriptionStatus: status,
        billingCycle: (subscription?.billing_cycle as BillingCycle) ?? 'monthly',
        nextBillingDate: periodEnd ?? null,
        stripeCustomerId: subscription?.stripe_customer_id ?? null,
        trialDaysRemaining: trialDays,
        paymentMethod: subscription?.payment_method
          ? (subscription.payment_method as PaymentMethod)
          : null,
        usage: {
          parcelsUsed: usageData?.parcels_used ?? 0,
          ...limits,
          apiCalls: usageData?.api_calls ?? 0,
          storageMb: usageData?.storage_mb ?? 0,
          surveysThisMonth: usageData?.surveys_this_month ?? 0,
        },
        invoices,
        loading: false,
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Kunde inte ladda faktureringsdata';
      set({ loading: false, error: message });
    }
  },

  // ─── Lightweight usage refresh (after operations) ───
  refreshUsage: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: usageData } = await supabase
        .from('usage_stats')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      const { currentPlan } = get();
      const limits = PLAN_LIMITS[currentPlan];

      set({
        usage: {
          parcelsUsed: usageData?.parcels_used ?? 0,
          ...limits,
          apiCalls: usageData?.api_calls ?? 0,
          storageMb: usageData?.storage_mb ?? 0,
          surveysThisMonth: usageData?.surveys_this_month ?? 0,
        },
      });
    } catch {
      // Non-critical
    }
  },

  // ─── Set/clear quota error for modal display ───
  setQuotaError: (err: QuotaError | null) => set({ quotaError: err }),

  // ─── Handle 402 quota errors from edge functions ───
  handleApiError: (error: unknown): boolean => {
    if (
      error &&
      typeof error === 'object' &&
      'context' in error
    ) {
      const ctx = (error as { context: { status?: number; body?: string } }).context;
      if (ctx?.status === 402) {
        try {
          const body = JSON.parse(ctx.body ?? '{}');
          if (body.details) {
            set({
              quotaError: {
                resource: body.details.metric ?? 'unknown',
                used: body.details.used ?? 0,
                limit: body.details.limit ?? 0,
              },
            });
            get().refreshUsage();
            return true;
          }
        } catch {
          // Fall through
        }
      }
    }
    return false;
  },

  // New plan purchases are retired; existing cancellation remains available.
  changePlan: async (planId: PlanId, _acceptance?: CheckoutAcceptance) => {
    if (planId === 'gratis') {
      await get().cancelSubscription();
      return;
    }
    set({ loading: false, error: 'New purchases are unavailable while payments are rebuilt.' });
  },

  // ─── Open Stripe Customer Portal to manage payment method ───
  updatePaymentMethod: async () => {
    set({ loading: true, error: null });
    try {
      const result = await invokeEdgeFunction<{ url: string }>('create-checkout-session', {
        portal: true,
      });

      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error('Kunde inte öppna betalningsportalen');
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Kunde inte uppdatera betalningsmetod';
      set({ loading: false, error: message });
    }
  },

  // ─── Cancel subscription ───
  cancelSubscription: async () => {
    set({ loading: true, error: null });
    try {
      await invokeEdgeFunction('cancel-subscription', {});
      // Reload billing data to reflect changes
      await get().loadBillingData();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Kunde inte avsluta prenumerationen';
      set({ loading: false, error: message });
    }
  },
}));

